import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { Modal, Row, Table } from 'antd';
import * as Icons from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';

import { useSocketFile } from '@/hooks/useSocketFile';
import { ListWithoutPlayer, ListWithoutPlayerProps } from './ListWithoutPlayer';
import type { FileFilterParams, FileFilterRequest } from '../types';
import { useApplicationContext } from '@/context/Application';
import { useDownloadVideo } from '@/hooks/useDownloadVideo';
import { ErgonomicToolsModal } from './ErgonomicToolsModal';
import { PaginationCustom } from '@/components/Pagination';
import { useDeleteFile } from '@/hooks/useDeleteFile';
import { useListFiles } from '@/hooks/useListFiles';
import { ListWithPlayer } from './ListWithPlayer';
import { EditFileModal } from './EditFileModal';
import { FilesListResponse } from '@/hooks';
import { TableContainer } from '../styles';

type DownloadVideo = {
	id: string;
	original_name: string;
};

type EditFileModalOpen = {
	isOpen: boolean;
	fileId?: string;
};

type SelectedReport = {
	visibleModal: boolean;
	file: FilesListResponse | null;
};

interface ListProps {
	isList: boolean;
	selectedRowKeys: string[];
	queryParams: FileFilterParams;
	onSelectRowKeys: (rows: string[]) => void;
}

export function List({ isList, queryParams, selectedRowKeys, onSelectRowKeys }: Readonly<ListProps>) {
	const history = useHistory();
	const { search } = useLocation();
	const searchParams = new URLSearchParams(search);
	const { organization, company } = useApplicationContext();

	const filter = formatFilterValues();
	const page = queryParams.page ? +queryParams.page : 1;
	const limit = queryParams.rows ? +queryParams.rows : 10;
	const offset = page === 0 ? 0 : page - 1;

	const {
		isLoading,
		isFetching,
		data: files
	} = useListFiles({
		organization_id: organization?.id,
		company_id: company?.id,
		filter: {
			...filter,
			limit,
			offset
		}
	});

	const { file } = useSocketFile<any>({
		organization_id: organization?.id!!,
		company_id: company?.id!!
	});

	const { mutateAsync: downloadVideo } = useDownloadVideo();

	const { mutateAsync: deleteFile, isLoading: deletingFile } = useDeleteFile({
		organization_id: organization?.id,
		company_id: company?.id
	});

	const [data, setData] = useState<FilesListResponse[]>([]); // List of files for update with socket.io
	const [selectedReport, setSelectedReport] = useState<SelectedReport>({
		file: null,
		visibleModal: false
	});

	useEffect(() => {
		onSelectRowKeys([]);
	}, [files]);

	const [editFileModal, setEditFileModal] = useState<EditFileModalOpen>({
		isOpen: false,
		fileId: ''
	});

	async function handleDownloadVideo(file: DownloadVideo): Promise<void> {
		const data = {
			file_id: file.id,
			company_id: company.id,
			organization_id: organization.id,
			original_name: file.original_name
		};
		await downloadVideo(data);
	}

	async function handleDeleteFile(fileId: string): Promise<void> {
		Modal.confirm({
			title: I18n.get('Heads up! Want to delete this file?'),
			icon: <Icons.ExclamationCircleOutlined />,
			okText: I18n.get('Confirm'),
			cancelText: I18n.get('Cancel'),
			okType: 'danger',
			onOk: async () => {
				await deleteFile(fileId);
			}
		});
	}

	function handleEditFile(fileId?: string, event?: React.MouseEvent<HTMLElement, MouseEvent>): void {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		setEditFileModal({
			fileId,
			isOpen: !editFileModal.isOpen
		});
	}

	function handleRedirectPreliminary(file: FilesListResponse): void {
		const hierarchy_params = `company_id=${file.company_id}&organization_id=${file.organization_id}`;
		const win = window.open(`/reporting/preliminary-ergonomic-analysis/${file.id}?${hierarchy_params}`, '_blank');
		win?.focus();
	}

	function handleSelectRow(id: string): void {
		const selectedRows = [...selectedRowKeys];

		if (selectedRows.indexOf(id) >= 0) {
			selectedRows.splice(selectedRows.indexOf(id), 1);
		} else {
			selectedRows.push(id);
		}
		handleSelectRowKeys(selectedRows);
	}

	function handleSelectRowKeys(selectedRows: string[]): void {
		onSelectRowKeys(selectedRows);
	}

	function handleRowsPerPageChange(rows: number): void {
		searchParams.set('page', `${1}`);
		searchParams.set('rows', `${rows ?? 10}`);

		history.push({
			search: searchParams.toString()
		});
	}

	function handlePageChange(page: number): void {
		searchParams.set('page', `${page ?? 1}`);
		history.push({ search: searchParams.toString() });
	}

	function handleOpenCreateReportModal(file: FilesListResponse): void {
		setSelectedReport((current) => ({
			...current,
			file: file,
			visibleModal: true
		}));
	}

	function handleCloseReportsModal(): void {
		setSelectedReport((current) => ({
			...current,
			file: null,
			visibleModal: false
		}));
	}

	function formatFilterValues(): FileFilterRequest {
		const createdAt = queryParams.created_at;

		if (createdAt) {
			const start_date = moment(createdAt[0]).format();
			const end_date = moment(createdAt[1]).format();

			return { ...queryParams, start_date, end_date };
		}

		return queryParams;
	}

	function handleUpdateFile(file: any): void {
		setData((prevState) => {
			// Check if file already exists in the current state
			const fileExists = prevState.some((f) => f.id === file.id);

			if (fileExists) {
				/*
					Necessary to update the file status and the workstation name
					workstations: file.workstations
				*/
				return prevState.map((f) => (f.id === file.id ? { ...f, status: file.status } : f));
			}
			// If file doesn't exist, add it to the beginning of the array
			return [file, ...prevState];
		});
	}

	useEffect(() => {
		setData(files.rows);
	}, [files.rows]);

	useEffect(() => {
		if (file) {
			handleUpdateFile(file);
		}
	}, [file]);

	const properties: ListWithoutPlayerProps = {
		files: data,
		onEditFile: handleEditFile,
		onSelectRow: handleSelectRow,
		selectedRows: selectedRowKeys,
		onDeleteFile: handleDeleteFile,
		onDownloadVideo: handleDownloadVideo,
		loading: deletingFile || isLoading || isFetching,
		onRedirectPreliminary: handleRedirectPreliminary,
		onShowReportOptionsReports: handleOpenCreateReportModal
	};

	if (isLoading || isFetching) {
		return <Table loading />;
	}

	return (
		<Row>
			<TableContainer span={24}>
				{isList ? (
					<ListWithoutPlayer {...properties} />
				) : (
					<Row gutter={[0, 10]}>
						{files?.rows?.map((file) => (
							<ListWithPlayer file={file} onSelectRowKey={handleSelectRowKeys} {...properties} />
						))}
					</Row>
				)}
				<PaginationCustom
					current={page}
					pageSize={limit}
					total={files.count}
					onChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
				/>
			</TableContainer>
			{editFileModal.isOpen && (
				<EditFileModal
					centered
					width={550}
					footer={null}
					open={editFileModal.isOpen}
					fileId={editFileModal.fileId}
					onCancel={(event) => handleEditFile(undefined, event)}
				/>
			)}
			{selectedReport.visibleModal && selectedReport.file && (
				<ErgonomicToolsModal
					file={selectedReport.file}
					onClose={handleCloseReportsModal}
					visible={selectedReport.visibleModal}
				/>
			)}
		</Row>
	);
}
